import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  List,
  createStyles,
  Theme,
  makeStyles,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import SidebarNav from './SidebarNav';
import DrawerSection, { drawerWidth } from './DrawerSection';
import { RootState } from '../../store';
import { UserState } from '../../store/user/types';
import { RouteContext, SidebarThemeContext } from '../../context';
import { SettingsSidebarBackToggle } from './SettingsSidebarBackToggle';
import { BANNER_HEIGHT } from '../../constants/uiConsts';
import { useSidebarTheme } from './useSidebarTheme';
import { SidebarLogo } from './SidebarLogo';

export { drawerWidth };

const SidebarSkeletonItems = [
  { id: 'crm' },
  { id: 'messaging' },
  { id: 'files' },
];
interface SidebarContainerStyleProps {
  showBanner: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidebarHeader: {
      padding: theme.spacing(2, 1, 3.5, 1),
      width: 'auto',
    },
    sidebarItemSkeleton: {
      borderRadius: theme.shape.borderRadius,
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: theme.spacing(0.5),
      height: '100%',
      [theme.breakpoints.up('md')]: {
        marginBottom: (props: SidebarContainerStyleProps) =>
          props.showBanner ? `${BANNER_HEIGHT}px` : 0,
      },
    },
  }),
);

interface SidebarProps {
  user: UserState;
  openMobile: boolean;
  onMobileClose: () => void;
}

const SidebarContainer: React.FC<SidebarProps> = ({
  user,
  openMobile,
  onMobileClose,
}) => {
  const { sidebarTheme } = useSidebarTheme();
  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );

  const classes = useStyles({
    showBanner: Boolean(bannerOptions),
  });
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isSettingsPage = pathname.includes('settings');
  return (
    <SidebarThemeContext.Provider value={sidebarTheme}>
      <DrawerSection onClose={onMobileClose} open={openMobile}>
        <Box className={classes.sidebarHeader}>
          {/* On main sidebar show portal logo */}
          <SidebarLogo isSettingsPage={isSettingsPage} />
          {/* On settings sidebar show back to portal button only on desktop */}
          {isSettingsPage && !isSmScreen && <SettingsSidebarBackToggle />}
        </Box>
        <List className={classes.list}>
          {!user.loaded || user.loading ? (
            SidebarSkeletonItems.map(({ id }) => (
              <Box key={id} py={0.5} px={1}>
                <Skeleton variant="rect" width="100%" height={28} />
              </Box>
            ))
          ) : (
            <SidebarNav />
          )}
        </List>
      </DrawerSection>
    </SidebarThemeContext.Provider>
  );
};

export default SidebarContainer;
